import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Alert, Empty, Spin, message } from "antd";
import { fetchSprNumberCatList } from "../number-cat/spr-number-cat-api";
import { fetchSprNumberSprList } from "../number-spr/spr-number-spr-api";
import {
  fetchSprNumberPricePrim,
  replaceSprNumberPricePrim
} from "./spr-number-price-api";
import {
  PRIM_TABS,
  normalizePrim,
  pick,
  toNumber
} from "./spr-number-price-utils";
import { PrimKindIcon, primKindUi } from "./spr-number-price-prim-ui";

const emptyByVid = () => {
  const map = {};
  for (const tab of PRIM_TABS) map[tab.vid] = [];
  return map;
};

/**
 * Применимость строки прайса — разделы справочников, клик по карточке сразу сохраняет.
 */
const SprNumberPricePrim = forwardRef(function SprNumberPricePrim(
  { idNumberPrice, idStr, embedded = false, disabled = false, onChanged },
  ref
) {
  const [optionsByVid, setOptionsByVid] = useState({});
  const [idsByVid, setIdsByVid] = useState(emptyByVid);
  const [loading, setLoading] = useState(false);
  const [savingVid, setSavingVid] = useState(null);
  const [error, setError] = useState("");

  const loadLookups = useCallback(async () => {
    const next = {};
    await Promise.all(
      PRIM_TABS.map(async (tab) => {
        if (tab.source === "number-cat") {
          const data = await fetchSprNumberCatList();
          next[tab.vid] = (Array.isArray(data) ? data : [])
            .map((row) => {
              const id = toNumber(pick(row, "idNumberCat", "IdNumberCat"));
              const name = pick(row, "name", "Name") ?? "";
              if (id == null) return null;
              return { id, name };
            })
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name, "ru"));
          return;
        }

        const data = await fetchSprNumberSprList(tab.sprVid);
        const items = (Array.isArray(data) ? data : [])
          .map((row) => {
            const id = toNumber(pick(row, "id", "Id"));
            const name = pick(row, "name", "Name") ?? "";
            if (id == null) return null;
            return { id, name };
          })
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name, "ru"));

        if (tab.zeroLabel) {
          next[tab.vid] = [{ id: 0, name: tab.zeroLabel }, ...items];
        } else {
          next[tab.vid] = items;
        }
      })
    );
    return next;
  }, []);

  const loadActive = useCallback(async () => {
    if (idStr == null) return emptyByVid();
    const data = await fetchSprNumberPricePrim(idNumberPrice, idStr);
    const map = emptyByVid();
    for (const row of (Array.isArray(data) ? data : []).map(normalizePrim)) {
      if (row.vid == null || row.id == null) continue;
      if (!map[row.vid]) map[row.vid] = [];
      map[row.vid].push(row.id);
    }
    return map;
  }, [idNumberPrice, idStr]);

  useEffect(() => {
    if (idNumberPrice == null) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([loadLookups(), loadActive()])
      .then(([opts, active]) => {
        if (cancelled) return;
        setOptionsByVid(opts);
        setIdsByVid(active);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить применимость");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idNumberPrice, idStr, loadLookups, loadActive]);

  const persistVid = async (targetIdStr, nextVid, ids) => {
    await replaceSprNumberPricePrim(idNumberPrice, targetIdStr, { vid: nextVid, ids });
  };

  const toggleId = async (nextVid, id) => {
    if (disabled || savingVid != null) return;
    const prev = idsByVid[nextVid] || [];
    const cur = new Set(prev);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    const next = [...cur];
    setIdsByVid((state) => ({ ...state, [nextVid]: next }));
    setError("");
    if (idStr == null) return;
    setSavingVid(nextVid);
    try {
      await persistVid(idStr, nextVid, next);
      onChanged?.();
    } catch (e) {
      setIdsByVid((state) => ({ ...state, [nextVid]: prev }));
      const text = e.message || "Не удалось сохранить применимость";
      setError(text);
      message.error(text);
    } finally {
      setSavingVid(null);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      saveTo: async (targetIdStr) => {
        if (idNumberPrice == null || targetIdStr == null || idStr != null) return false;
        let changed = false;
        for (const tab of PRIM_TABS) {
          const ids = idsByVid[tab.vid] || [];
          if (ids.length === 0) continue;
          await persistVid(targetIdStr, tab.vid, ids);
          changed = true;
        }
        if (changed) onChanged?.();
        return changed;
      }
    }),
    [idNumberPrice, idStr, idsByVid, onChanged]
  );

  const className = `spr-number-price-prim${embedded ? " spr-number-price-prim--embedded" : ""}`;

  if (loading) {
    return (
      <div className={`${className} spr-number-price-prim--center`}>
        <Spin tip="Загрузка применимости..." />
      </div>
    );
  }

  return (
    <div className={className}>
      {error ? (
        <Alert
          className="spr-number-price-prim__error"
          type="error"
          showIcon
          message={error}
        />
      ) : null}
      {PRIM_TABS.map((tab) => {
        const options = optionsByVid[tab.vid] || [];
        const selectedIds = idsByVid[tab.vid] || [];
        const busy = disabled || savingVid === tab.vid;
        return (
          <section key={tab.vid} className="spr-number-price-prim__section">
            <h5
              className="spr-number-price-prim__section-title"
              style={{ color: primKindUi(tab.vid).color }}
            >
              <PrimKindIcon vid={tab.vid} />
              {tab.label}
            </h5>
            <div className="spr-number-price-prim__list">
              {options.length === 0 ? (
                <Empty description="Нет элементов справочника" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                options.map((opt) => {
                  const on = selectedIds.includes(opt.id);
                  return (
                    <button
                      key={`${tab.vid}-${opt.id}`}
                      type="button"
                      className={`spr-number-price-prim__card${on ? " spr-number-price-prim__card--on" : ""}`}
                      aria-pressed={on}
                      disabled={busy}
                      onClick={() => toggleId(tab.vid, opt.id)}
                    >
                      {opt.name || `#${opt.id}`}
                    </button>
                  );
                })
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
});

export default SprNumberPricePrim;
